import { useEffect, useRef, useState } from "react";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, FileSignature, ShieldCheck, AlertCircle, CheckCircle2, Share2 } from "lucide-react";
import { SignaturePad, type SignaturePadHandle } from "@/components/signature-pad";

interface BootstrapResponse {
  documentName: string;
  agencyName: string;
  agencyLogoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  hasFile: boolean;
  recipient: { name: string; email: string };
  token: string;
}

interface SignedResponse {
  success: true;
  documentName: string;
  signedAt: string;
  auditHash: string;
}

export default function SignPage() {
  const params = useParams<{ token: string }>();
  const token = params.token;

  const [boot, setBoot] = useState<BootstrapResponse | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(true);

  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [agree, setAgree] = useState(false);
  const [method, setMethod] = useState<"drawn" | "typed">("drawn");
  const [typedName, setTypedName] = useState("");
  const [drawnEmpty, setDrawnEmpty] = useState(true);

  const padRef = useRef<SignaturePadHandle>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<SignedResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/sign/${encodeURIComponent(token)}`, { credentials: "omit" });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (!cancelled) setBootError(data?.message || "This signing link is no longer valid.");
          return;
        }
        const data = (await res.json()) as BootstrapResponse;
        if (cancelled) return;
        setBoot(data);
        setSignerName(data.recipient.name || "");
        setSignerEmail(data.recipient.email || "");
        setTypedName(data.recipient.name || "");
        document.title = `Sign: ${data.documentName} — ${data.agencyName}`;
      } catch {
        if (!cancelled) setBootError("Couldn't load this document. Please try again later.");
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!signerName.trim()) { setSubmitError("Please enter your full legal name."); return; }
    if (!signerEmail.trim()) { setSubmitError("Please enter your email address."); return; }
    if (!agree) { setSubmitError("You must agree to the terms before signing."); return; }

    let signatureDataUrl: string | null = null;
    let typedNameOut: string | null = null;
    if (method === "drawn") {
      signatureDataUrl = padRef.current?.getDataUrl() || null;
      if (!signatureDataUrl) { setSubmitError("Please draw your signature."); return; }
    } else {
      if (!typedName.trim()) { setSubmitError("Please type your name as your signature."); return; }
      typedNameOut = typedName.trim();
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/sign/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signedByName: signerName.trim(),
          signedByEmail: signerEmail.trim(),
          agree: true,
          signatureMethod: method,
          signatureDataUrl,
          typedName: typedNameOut,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data?.message || "Could not save your signature. Please try again.");
        return;
      }
      setDone(data as SignedResponse);
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (bootError || !boot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Link unavailable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground" data-testid="text-sign-error">
              {bootError || "Not found."}
            </p>
            <p className="text-sm text-muted-foreground">
              If you believe this is a mistake, contact the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" /> Signed successfully
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm" data-testid="text-sign-success">
              Thanks, {signerName.split(" ")[0] || "there"}. Your signature on{" "}
              <strong>{done.documentName}</strong> has been recorded.
            </p>
            <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1 font-mono">
              <div>Signed at: {new Date(done.signedAt).toLocaleString()}</div>
              <div>Audit hash: {done.auditHash.slice(0, 24)}…</div>
            </div>
            <p className="text-xs text-muted-foreground">
              A confirmation email has been sent to {signerEmail}. Save this
              audit reference for your records.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const headerStyle = boot.primaryColor
    ? { borderTopColor: boot.primaryColor, borderTopWidth: 4 }
    : undefined;
  const submitButtonStyle = boot.primaryColor
    ? { backgroundColor: boot.primaryColor, color: "#fff", borderColor: boot.primaryColor }
    : undefined;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Branded header */}
      <div className="border-b bg-background" style={headerStyle}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {boot.agencyLogoUrl ? (
              <img
                src={boot.agencyLogoUrl}
                alt={boot.agencyName}
                className="h-10 w-auto object-contain"
                data-testid="img-agency-logo"
              />
            ) : (
              <div
                className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center"
                style={boot.primaryColor ? { backgroundColor: `${boot.primaryColor}1a` } : undefined}
              >
                <FileSignature
                  className="h-5 w-5 text-primary"
                  style={boot.primaryColor ? { color: boot.primaryColor } : undefined}
                />
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">From</div>
              <div className="text-sm font-medium" data-testid="text-agency-name">
                {boot.agencyName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secure signing
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-document-name">
            {boot.documentName}
          </h1>
          <p className="text-sm text-muted-foreground">
            Review the document below, then sign at the bottom of the page.
          </p>
        </div>

        {/* Document preview */}
        <Card>
          <CardContent className="p-0">
            {boot.hasFile ? (
              <iframe
                src={`/api/sign/${encodeURIComponent(token)}/file`}
                title="Document preview"
                className="w-full h-[640px] rounded-md"
                data-testid="iframe-document-preview"
              />
            ) : (
              <div className="p-12 text-center text-muted-foreground">
                <FileSignature className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  No file is attached to this document. Your signature below will
                  serve as your acceptance of the document named above.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signing form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your signature</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signer-name">Full legal name</Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={e => setSignerName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    data-testid="input-signer-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signer-email">Email address</Label>
                  <Input
                    id="signer-email"
                    type="email"
                    value={signerEmail}
                    onChange={e => setSignerEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="input-signer-email"
                  />
                </div>
              </div>

              <Tabs value={method} onValueChange={v => setMethod(v as "drawn" | "typed")}>
                <TabsList className="grid grid-cols-2 w-full sm:w-auto">
                  <TabsTrigger value="drawn" data-testid="tab-draw">Draw signature</TabsTrigger>
                  <TabsTrigger value="typed" data-testid="tab-type">Type signature</TabsTrigger>
                </TabsList>
                <TabsContent value="drawn" className="mt-3">
                  <SignaturePad
                    ref={padRef}
                    height={180}
                    onChange={isEmpty => setDrawnEmpty(isEmpty)}
                  />
                </TabsContent>
                <TabsContent value="typed" className="mt-3 space-y-2">
                  <Input
                    value={typedName}
                    onChange={e => setTypedName(e.target.value)}
                    placeholder="Type your name as your signature"
                    className="text-2xl h-14"
                    style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}
                    data-testid="input-typed-signature"
                  />
                  <p className="text-xs text-muted-foreground">
                    By typing your name above, you agree it is the legal
                    equivalent of your handwritten signature.
                  </p>
                </TabsContent>
              </Tabs>

              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="agree"
                  checked={agree}
                  onCheckedChange={v => setAgree(v === true)}
                  data-testid="checkbox-agree"
                />
                <Label htmlFor="agree" className="text-sm font-normal leading-snug cursor-pointer">
                  I have read this document and agree that my electronic
                  signature is the legal equivalent of my handwritten signature.
                </Label>
              </div>

              {submitError && (
                <Alert variant="destructive" data-testid="alert-submit-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2 pt-2 flex-wrap">
                {typeof navigator !== "undefined" && typeof (navigator as any).share === "function" && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="button-share-sign-link"
                    onClick={async () => {
                      try {
                        await (navigator as any).share({
                          title: "Document to sign",
                          text: "Please review and sign this document.",
                          url: window.location.href,
                        });
                      } catch (e) { /* user cancelled */ }
                    }}
                  >
                    <Share2 className="h-4 w-4 mr-2" /> Share link
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={
                    submitting ||
                    !agree ||
                    !signerName.trim() ||
                    !signerEmail.trim() ||
                    (method === "drawn" && drawnEmpty) ||
                    (method === "typed" && !typedName.trim())
                  }
                  style={submitButtonStyle}
                  data-testid="button-submit-signature"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</>
                  ) : (
                    <>Sign and submit</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        <p className="text-xs text-center text-muted-foreground py-4">
          This signing session is secured by a one-time link. Your IP address
          and browser are recorded with the signature for audit purposes.
        </p>
      </div>
    </div>
  );
}
